import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingBagIcon } from '@heroicons/react/24/outline';
import { shopApi } from '../../../utils/shopApi';
import ShopTabs from './ShopTabs';
import { PageHeader, FilterChips, Skeleton, EmptyState, ErrorState } from '../../../components/ui';
import { formatDate } from '../../../utils/dates';
import '../booking-shared.css';
import './shop.css';

// An order's status: its words and its pill (amber while waiting on the
// club, green once it's there to collect, grey when done)
const STATUS = {
  ORDERED: ['Order placed', 'wait'],
  ARRIVED: ['Arrived at club', 'good'],
  FULFILLED: ['Collected', 'off'],
};

const ITEM_STATUS = {
  AWAITING: ['Awaiting', 'wait'],
  ORDERED_FROM_SUPPLIER: ['Ordered from supplier', 'wait'],
  ARRIVED: ['Arrived at club', 'good'],
  FULFILLED: ['Collected', 'off'],
};

const pill = ([label, tone] = []) => label && <span className={`bk-pill bk-pill--${tone}`}>{label}</span>;

const FILTERS = [
  { id: 'ALL', label: 'All' },
  ...Object.entries(STATUS).map(([id, [label]]) => ({ id, label })),
];

export default function MyOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [filter, setFilter] = useState('ALL');

  const load = useCallback(() => {
    setLoading(true);
    setLoadError(false);
    shopApi.getMyOrders()
      .then(res => setOrders(res.data))
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = filter === 'ALL' ? orders : orders.filter(o => o.status === filter);

  return (
    <div className="bk-page bk-page--narrow">
      <PageHeader title="My orders" />
      <ShopTabs />

      {loadError ? (
        <ErrorState title="Couldn't load your orders" onRetry={load} />
      ) : loading ? (
        <Skeleton rows={3} label="Loading orders…" />
      ) : orders.length === 0 ? (
        <EmptyState
          icon={ShoppingBagIcon}
          title="No orders yet"
          text="Kit you order from the shop shows here until you collect it."
          action={<Link to="/booking/shop" className="bk-btn bk-btn--primary">Browse kit</Link>}
        />
      ) : (
        <>
          <FilterChips label="Show orders" chips={FILTERS} value={filter} onChange={setFilter} />
          {filtered.length === 0 && <p className="bk-muted">No orders here.</p>}
          {filtered.map(order => (
            <div key={order.id} className="bk-card">
              <div className="shop-order-card-header">
                {pill(STATUS[order.status])}
                <span className="shop-order-meta">{formatDate(order.createdAt)}</span>
              </div>
              <div className="shop-order-items">
                {order.items.map((item, i) => (
                  <div key={i} className="shop-order-item">
                    <span className="shop-order-item__name">
                      {item.productName}, {item.size}
                      {item.customisation ? ` (${item.customisation})` : ''}
                      {item.quantity > 1 ? `, qty ${item.quantity}` : ''}
                    </span>
                    {item.status && pill(ITEM_STATUS[item.status])}
                  </div>
                ))}
              </div>
              <p className="shop-order-total">Total: £{(order.total / 100).toFixed(2)}</p>
              <p className="shop-order-meta shop-order-ref">Ref: {order.id}</p>
            </div>
          ))}
        </>
      )}
    </div>
  );
}
